// Componentes de "evolución temporal" compartidos por VerReporteAsistentas y
// VerReporteSupervisoras: línea de cumplimiento, calendario, mini-tendencia por
// supervisora, evolución de preguntas (% de respuestas en verde) y el aviso de
// pisos con 0 reportes en el periodo seleccionado.
import React, { useEffect, useMemo, useRef, useState } from "react";
import { C } from "../analisis/comun";
import { numEstrellas } from "../../utils/estrellas";

const TXT = { primario: "#1e293b", secundario: "#475569", tenue: "#94a3b8", rejilla: "#e2e8f0" };
const COLOR_EXTRA = "#7c3aed";
// Escala del calendario: <50 rojos (más oscuro cuanto más bajo), 50–69 naranja→amarillo, ≥70 verdes (claro→oscuro)
const mezcla = (a, b, t) => a.map((ai, i) => Math.round(ai + (b[i] - ai) * Math.max(0, Math.min(1, t))));
const colorCalendario = (pct) => {
  let rgb;
  if (pct < 50) rgb = mezcla([127, 29, 29], [248, 113, 113], pct / 49);
  else if (pct < 70) rgb = mezcla([249, 115, 22], [250, 204, 21], (pct - 50) / 19);
  else rgb = mezcla([187, 247, 208], [20, 83, 45], (pct - 70) / 30);
  const lum = (0.299 * rgb[0] + 0.587 * rgb[1] + 0.114 * rgb[2]) / 255;
  return { bg: `rgb(${rgb.join(",")})`, texto: lum > 0.62 ? "#1e293b" : "#fff" };
};
const colorPct = (pct) => (pct == null ? "#cbd5e1" : pct >= 80 ? C.verde : pct >= 50 ? C.amarillo : C.rojo);
const fmtDia = (iso) => new Date(iso + "T00:00:00").toLocaleDateString("es-ES", { weekday: "short", day: "2-digit", month: "short" });
const isoLocal = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const lunesDe = (iso) => {
  const d = new Date(iso + "T00:00:00");
  const dow = (d.getDay() + 6) % 7;
  d.setDate(d.getDate() - dow);
  return isoLocal(d);
};
const addDiasISO = (iso, n) => { const d = new Date(iso + "T00:00:00"); d.setDate(d.getDate() + n); return isoLocal(d); };

// Tooltip flotante común
const Tip = ({ tip }) => tip ? (
  <div style={{
    position: "fixed", left: Math.min(tip.x + 14, window.innerWidth - 230), top: tip.y + 14, zIndex: 1000,
    background: "#0f172a", color: "#fff", borderRadius: 8, padding: "8px 10px", fontSize: 12, lineHeight: 1.45,
    pointerEvents: "none", boxShadow: "0 6px 20px rgba(15,23,42,0.25)", maxWidth: 240,
  }}>
    {tip.lines.map((l, i) => <div key={i} style={i === 0 ? { fontWeight: 700, marginBottom: 2 } : undefined}>{l}</div>)}
  </div>
) : null;

// Botón de dos posiciones (Línea / Barras) a juego con el estilo índigo del reporte
export const ToggleVista = ({ valor, opciones, onChange }) => (
  <div style={{ display: "inline-flex", background: "#eef2ff", borderRadius: 999, padding: 3, border: "2px solid #c7d2fe" }}>
    {opciones.map((o) => (
      <button key={o.k} onClick={() => onChange(o.k)} style={{
        border: "none", borderRadius: 999, padding: "5px 12px", fontSize: 12.5, fontWeight: 800, cursor: "pointer",
        background: valor === o.k ? "#4338ca" : "transparent", color: valor === o.k ? "#fff" : "#3730a3",
      }}>{o.label}</button>
    ))}
  </div>
);

// Agrega por semana (lunes) si hay muchos días
function agrupar(dias, semanal = dias.length > 45) {
  if (!semanal) return { puntos: dias.map((d) => ({ ...d, clave: d.fecha, etiqueta: fmtDia(d.fecha) })), porSemana: false };
  const m = new Map();
  dias.forEach((d) => {
    const k = lunesDe(d.fecha);
    if (!m.has(k)) m.set(k, { fecha: k, enviados: 0, total: 0, extra: 0 });
    const e = m.get(k); e.enviados += d.enviados; e.total += d.total; e.extra += d.extra || 0;
  });
  const puntos = Array.from(m.values()).sort((a, b) => a.fecha.localeCompare(b.fecha))
    .map((e) => ({ ...e, pct: e.total ? Math.round((e.enviados / e.total) * 100) : null, clave: e.fecha, etiqueta: `Semana del ${fmtDia(e.fecha)}` }));
  return { puntos, porSemana: true };
}

// ── 1. Evolución del cumplimiento (línea) ─────────────────────────────────
// media (opcional): días de TODAS las supervisoras; se pinta como línea negra
// discontinua de referencia en lugar de la media móvil.
export function EvolucionCumplimiento({ dias, media }) {
  const [tip, setTip] = useState(null);
  const [hover, setHover] = useState(null);
  const semanal = (media || dias).length > 45;
  const { puntos, porSemana } = useMemo(() => agrupar(dias, semanal), [dias, semanal]);
  const refPorClave = useMemo(() => (media ? new Map(agrupar(media, semanal).puntos.map((p) => [p.clave, p.pct])) : null), [media, semanal]);
  const ref = refPorClave ? puntos.map((p) => refPorClave.get(p.clave) ?? null) : null;
  const hayExtras = puntos.some((p) => p.extra > 0);
  const maxExtra = Math.max(1, ...puntos.map((p) => p.extra || 0));
  const W = 720, H = 220, PL = 38, PR = 16, PT = 12, PB = 28, HE = hayExtras ? 46 : 0;
  const n = puntos.length;
  const anchoBarra = Math.max(3, Math.min(16, ((W - PL - PR) / Math.max(1, n)) * 0.6));
  const x = (i) => PL + (n <= 1 ? (W - PL - PR) / 2 : (i * (W - PL - PR)) / (n - 1));
  const y = (v) => PT + (H - PT - PB) * (1 - v / 100);
  // media móvil (7 días o 3 semanas), solo con puntos con dato
  const ventana = porSemana ? 3 : 7;
  const movil = ref ? puntos.map(() => null) : puntos.map((_, i) => {
    const tramo = puntos.slice(Math.max(0, i - ventana + 1), i + 1).filter((p) => p.total > 0);
    const env = tramo.reduce((a, p) => a + p.enviados, 0), tot = tramo.reduce((a, p) => a + p.total, 0);
    return tot ? Math.round((env / tot) * 100) : null;
  });
  const cada = Math.max(1, Math.ceil(n / 8));
  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    const px = ((e.clientX - r.left) / r.width) * W;
    const i = Math.max(0, Math.min(n - 1, Math.round(((px - PL) / (W - PL - PR)) * (n - 1))));
    const p = puntos[i];
    setHover(i);
    setTip({ x: e.clientX, y: e.clientY, lines: [
      p.etiqueta, p.total ? `${p.enviados}/${p.total} enviados · ${p.pct}%` : "Sin visitas obligatorias",
      ...(p.extra ? [`✨ +${p.extra} extra`] : []), ...(ref && ref[i] != null ? [`Media de todas: ${ref[i]}%`] : []), ...(movil[i] != null ? [`Media ${ventana} ${porSemana ? "semanas" : "días"}: ${movil[i]}%`] : []),
    ] });
  };
  const conDato = puntos.map((p, i) => ({ p, i })).filter(({ p }) => p.pct != null);
  return (
    <div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 14, fontSize: 11.5, color: TXT.secundario, marginBottom: 6 }}>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><span style={{ width: 16, height: 3, borderRadius: 2, background: "#4338ca" }} />% cumplimiento {porSemana ? "por semana" : "por día"}</span>
        {ref ? (
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><span style={{ width: 18, height: 0, borderTop: "2px dashed #111827" }} />Media de todas las supervisoras</span>
        ) : (
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><span style={{ width: 16, height: 0, borderTop: "2px dotted #94a3b8" }} />Media {ventana} {porSemana ? "semanas" : "días"}</span>
        )}
        {hayExtras && <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><span style={{ width: 10, height: 10, borderRadius: 2, background: COLOR_EXTRA }} />✨ Extras (aparte, no cuentan en el %)</span>}
      </div>
      <svg viewBox={`0 0 ${W} ${H + HE}`} style={{ width: "100%", height: "auto", display: "block" }} onMouseMove={onMove} onMouseLeave={() => { setTip(null); setHover(null); }}>
        {[0, 25, 50, 75, 100].map((t) => (
          <g key={t}>
            <line x1={PL} x2={W - PR} y1={y(t)} y2={y(t)} stroke={TXT.rejilla} />
            <text x={PL - 6} y={y(t) + 4} fontSize={10} fill={TXT.tenue} textAnchor="end">{t}%</text>
          </g>
        ))}
        {puntos.map((p, i) => (i % cada === 0 || i === n - 1) && (
          <text key={p.clave} x={x(i)} y={H - 8} fontSize={10} fill={TXT.tenue} textAnchor="middle">{p.fecha.slice(8, 10)}/{p.fecha.slice(5, 7)}</text>
        ))}
        {hover != null && <line x1={x(hover)} x2={x(hover)} y1={PT} y2={H - PB} stroke={TXT.tenue} strokeDasharray="3 3" />}
        {ref ? (
          <polyline fill="none" stroke="#111827" strokeWidth={1.8} strokeDasharray="6 4"
            points={ref.map((v, i) => (v == null ? null : `${x(i)},${y(v)}`)).filter(Boolean).join(" ")} />
        ) : (
          <polyline fill="none" stroke="#94a3b8" strokeWidth={1.5} strokeDasharray="2 3"
            points={movil.map((v, i) => (v == null ? null : `${x(i)},${y(v)}`)).filter(Boolean).join(" ")} />
        )}
        <polyline fill="none" stroke="#4338ca" strokeWidth={2} strokeLinejoin="round" strokeLinecap="round"
          points={conDato.map(({ p, i }) => `${x(i)},${y(p.pct)}`).join(" ")} />
        {conDato.map(({ p, i }) => (
          <circle key={p.clave} cx={x(i)} cy={y(p.pct)} r={hover === i ? 5.5 : 4} fill={colorPct(p.pct)} stroke="#fff" strokeWidth={2} />
        ))}
        {hayExtras && (
          <g>
            <text x={PL - 6} y={H + 26} fontSize={10} fill={COLOR_EXTRA} textAnchor="end" fontWeight={700}>✨</text>
            <line x1={PL} x2={W - PR} y1={H + HE - 4} y2={H + HE - 4} stroke={TXT.rejilla} />
            {puntos.map((p, i) => p.extra > 0 && (
              <g key={p.clave}>
                <rect x={x(i) - anchoBarra / 2} width={anchoBarra} y={H + HE - 4 - (p.extra / maxExtra) * 26} height={(p.extra / maxExtra) * 26} rx={2} fill={COLOR_EXTRA} opacity={hover === i ? 1 : 0.85} />
                {(n <= 31 || p.extra === maxExtra) && <text x={x(i)} y={H + HE - 8 - (p.extra / maxExtra) * 26} fontSize={9.5} fill={TXT.secundario} textAnchor="middle" fontWeight={700}>{p.extra}</text>}
              </g>
            ))}
          </g>
        )}
      </svg>
      <Tip tip={tip} />
    </div>
  );
}

// ── 3. Mini-tendencia (sparkline + flecha) ────────────────────────────────
// serie: [{fecha, enviados, total}] ordenada. Flecha: 2ª mitad vs 1ª mitad del periodo.
export function MiniTendencia({ serie, media }) {
  const pts = serie.filter((d) => d.total > 0);
  if (pts.length < 2) return <span style={{ width: 128, display: "inline-block" }} />;
  const w = 96, h = 26;
  const xs = (i) => 2 + (i * (w - 4)) / (pts.length - 1);
  const ys = (d) => 2 + (h - 4) * (1 - (d.enviados / d.total));
  const mitad = Math.floor(pts.length / 2);
  const pct = (arr) => { const e = arr.reduce((a, d) => a + d.enviados, 0), t = arr.reduce((a, d) => a + d.total, 0); return t ? (e / t) * 100 : null; };
  const a = pct(pts.slice(0, mitad)), b = pct(pts.slice(mitad));
  const delta = a != null && b != null ? Math.round(b - a) : 0;
  const flecha = delta >= 5 ? { s: `▲ +${delta}`, c: C.verde, t: "Mejora en la 2ª mitad del periodo" }
    : delta <= -5 ? { s: `▼ ${delta}`, c: C.rojo, t: "Empeora en la 2ª mitad del periodo" }
    : { s: "→", c: TXT.tenue, t: "Estable en el periodo" };
  return (
    <span title={`${flecha.t} (${delta >= 0 ? "+" : ""}${delta} puntos)${media ? " · línea negra discontinua: media de todas" : ""}`} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
      <svg width={w} height={h} style={{ display: "block" }}>
        {media && (() => {
          const m = new Map(media.filter((d) => d.total > 0).map((d) => [d.fecha, d.enviados / d.total]));
          const ptsRef = pts.map((d, i) => (m.has(d.fecha) ? `${xs(i)},${2 + (h - 4) * (1 - m.get(d.fecha))}` : null)).filter(Boolean);
          return ptsRef.length > 1 && <polyline fill="none" stroke="#111827" strokeWidth={1.2} strokeDasharray="3 2" opacity={0.8} points={ptsRef.join(" ")} />;
        })()}
        <polyline fill="none" stroke="#4338ca" strokeWidth={1.8} strokeLinejoin="round" points={pts.map((d, i) => `${xs(i)},${ys(d)}`).join(" ")} />
        <circle cx={xs(pts.length - 1)} cy={ys(pts[pts.length - 1])} r={2.5} fill="#4338ca" />
      </svg>
      <span style={{ fontSize: 11.5, fontWeight: 800, color: flecha.c, minWidth: 30 }}>{flecha.s}</span>
    </span>
  );
}

export function seriePorDia(filas) {
  const m = new Map();
  filas.forEach((f) => {
    if (!m.has(f.fecha)) m.set(f.fecha, { fecha: f.fecha, enviados: 0, total: 0 });
    const e = m.get(f.fecha); e.total++; if (f.enviado) e.enviados++;
  });
  return Array.from(m.values()).sort((a, b) => a.fecha.localeCompare(b.fecha));
}

// ── 4. Calendario de cumplimiento ─────────────────────────────────────────
export function CalendarioCumplimiento({ dias }) {
  const [tip, setTip] = useState(null);
  if (!dias.length) return null;
  const porFecha = new Map(dias.map((d) => [d.fecha, d]));
  const ini = lunesDe(dias[0].fecha), fin = dias[dias.length - 1].fecha;
  const semanas = [];
  for (let l = ini; l <= fin; l = addDiasISO(l, 7)) semanas.push(l);
  const ESCALA = [
    { c: `linear-gradient(90deg, ${colorCalendario(0).bg}, ${colorCalendario(49).bg})`, t: "0–49 %" },
    { c: `linear-gradient(90deg, ${colorCalendario(50).bg}, ${colorCalendario(69).bg})`, t: "50–69 %" },
    { c: `linear-gradient(90deg, ${colorCalendario(70).bg}, ${colorCalendario(100).bg})`, t: "70–100 %" },
    { c: "#f1f5f9", t: "Sin visitas obligatorias" },
  ];
  return (
    <div style={{ overflowX: "auto" }}>
      <div style={{ display: "grid", gridTemplateColumns: `74px repeat(7, minmax(40px, 1fr))`, gap: 4, minWidth: 380 }}>
        <div />
        {["L", "M", "X", "J", "V", "S", "D"].map((d) => <div key={d} style={{ fontSize: 11, color: TXT.tenue, textAlign: "center", fontWeight: 700 }}>{d}</div>)}
        {semanas.map((lun) => (
          <React.Fragment key={lun}>
            <div style={{ fontSize: 11, color: TXT.secundario, alignSelf: "center", fontWeight: 600 }}>{new Date(lun + "T00:00:00").toLocaleDateString("es-ES", { day: "2-digit", month: "short" })}</div>
            {[0, 1, 2, 3, 4, 5, 6].map((k) => {
              const f = addDiasISO(lun, k); const d = porFecha.get(f);
              const fuera = f < dias[0].fecha || f > fin;
              const tiene = d && d.total > 0;
              const cc = tiene ? colorCalendario(d.pct) : null;
              const bg = fuera ? "transparent" : tiene ? cc.bg : d?.extra ? "#f3e8ff" : "#f1f5f9";
              return (
                <div key={f}
                  onMouseMove={(e) => !fuera && setTip({ x: e.clientX, y: e.clientY, lines: [fmtDia(f), tiene ? `${d.enviados}/${d.total} · ${d.pct}%` : "Sin visitas obligatorias", ...(d?.extra ? [`✨ +${d.extra} extra`] : [])] })}
                  onMouseLeave={() => setTip(null)}
                  style={{ minHeight: 38, padding: "2px 0", borderRadius: 7, background: bg, border: fuera ? "1px dashed #eef2f7" : "none", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", color: tiene ? cc.texto : TXT.tenue }}>
                  {!fuera && <span style={{ fontSize: 10, opacity: 0.85 }}>{f.slice(8, 10)}</span>}
                  {tiene && <span style={{ fontSize: 11.5, fontWeight: 800 }}>{d.pct}%</span>}
                  {!fuera && d?.extra > 0 && <span style={{ fontSize: 9.5, fontWeight: 800, color: tiene ? cc.texto : COLOR_EXTRA, lineHeight: 1 }}>✨{d.extra}</span>}
                </div>
              );
            })}
          </React.Fragment>
        ))}
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 12, marginTop: 10, fontSize: 11.5, color: TXT.secundario }}>
        {ESCALA.map((e) => <span key={e.t} style={{ display: "inline-flex", alignItems: "center", gap: 5 }}><span style={{ width: 28, height: 10, borderRadius: 3, background: e.c, border: "1px solid #e2e8f0" }} />{e.t}</span>)}
        {dias.some((d) => d.extra > 0) && <span style={{ display: "inline-flex", alignItems: "center", gap: 5, color: COLOR_EXTRA, fontWeight: 700 }}>✨N = reportes extra (aparte del %)</span>}
      </div>
      <Tip tip={tip} />
    </div>
  );
}

// ── 5. Evolución de las preguntas: % de respuestas en VERDE por semana/día ─
// Semáforo: verde / (verde+amarillo+rojo) ("desconoce" no cuenta). Sí/No: sí / total.
// Opciones de color: respuestas cuya opción tiene color verde / total. Estrellas: 4-5 estrellas / total.
const esVerde = (hex) => {
  const m = /^#?([0-9a-f]{6})$/i.exec(String(hex || "").trim());
  if (!m) return false;
  const n = parseInt(m[1], 16), r = (n >> 16) / 255, g = ((n >> 8) & 255) / 255, b = (n & 255) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b), d = max - min;
  if (d < 0.15 || max !== g) return false;
  const h = 60 * (((b - r) / d) + 2);
  return h >= 80 && h <= 170;
};
function verdesDePregunta(p, filas, contar) {
  const id = String(p.id);
  let verde = 0, total = 0;
  if (p.tipo === "opciones_color") {
    const idsVerdes = new Set((p.valores || []).filter((o) => esVerde(o.color)).map((o) => String(o.id)));
    filas.forEach((f) => {
      if (!f.enviado) return;
      const resumenAlumnos = f.respuestas_alumnos?.[id];
      if (resumenAlumnos) {
        Object.entries(resumenAlumnos).forEach(([v, nombres]) => {
          const n = (nombres || []).length; total += n; if (idsVerdes.has(String(v))) verde += n;
        });
      } else {
        const v = f.respuestas?.[id];
        if (v === undefined || v === null || v === "") return;
        // respuesta múltiple: cuenta cada opción marcada
        (Array.isArray(v) ? v : [v]).forEach((x) => { total++; if (idsVerdes.has(String(x))) verde++; });
      }
    });
  } else if (p.tipo === "estrellas") {
    // 4 o 5 estrellas = «verde»
    filas.forEach((f) => {
      if (!f.enviado) return;
      const resumenAlumnos = f.respuestas_alumnos?.[id];
      const sumar = (v, n) => { const e = numEstrellas(v); if (e === null) return; total += n; if (e >= 4) verde += n; };
      if (resumenAlumnos) Object.entries(resumenAlumnos).forEach(([v, nombres]) => sumar(v, (nombres || []).length));
      else sumar(f.respuestas?.[id], 1);
    });
  } else {
    const c = contar(filas, id);
    verde = c.verde; total = c.verde + c.amarillo + c.rojo;
  }
  return { verde, total, pct: total ? Math.round((verde / total) * 100) : null };
}
// Con su propio filtro de supervisora (independiente del del heatmap).
export function EvolucionPreguntas({ preguntas, filas, contar }) {
  const [sup, setSup] = useState("todas");
  const supervisoras = useMemo(() => [...new Set(filas.map((f) => f.user_supervisora).filter(Boolean))].sort(), [filas]);
  const filasSup = useMemo(() => (sup === "todas" ? filas : filas.filter((f) => f.user_supervisora === sup)), [filas, sup]);
  return (
    <div>
      {supervisoras.length > 1 && (
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
          <span style={{ fontSize: 12.5, fontWeight: 700, color: "#64748b" }}>👩‍💼 Supervisora:</span>
          <select value={sup} onChange={(e) => setSup(e.target.value)} style={{
            padding: "8px 14px", borderRadius: 12, border: "2px solid #c7d2fe", fontSize: 13,
            background: "linear-gradient(135deg,#fff,#eef2ff)", fontWeight: 800, color: "#3730a3",
            cursor: "pointer", boxShadow: "0 2px 8px rgba(79,70,229,0.15)", outline: "none",
          }}>
            <option value="todas">Todas las supervisoras</option>
            {supervisoras.map((x) => <option key={x} value={x}>{x}</option>)}
          </select>
        </div>
      )}
      <TablaEvolucionPreguntas preguntas={preguntas} filas={filasSup} contar={contar} />
    </div>
  );
}

function TablaEvolucionPreguntas({ preguntas, filas, contar }) {
  const [tip, setTip] = useState(null);
  const validas = preguntas.filter((p) => ["semaforo", "semaforo+desconozco", "si_no", "opciones_color", "estrellas"].includes(p.tipo));
  const fechas = [...new Set(filas.map((f) => f.fecha))].sort();
  const porDia = fechas.length <= 14;
  const columnas = porDia ? fechas : [...new Set(fechas.map(lunesDe))].sort();
  const grupos = useMemo(() => {
    const m = new Map(columnas.map((c) => [c, []]));
    filas.forEach((f) => { const k = porDia ? f.fecha : lunesDe(f.fecha); if (m.has(k)) m.get(k).push(f); });
    return m;
  }, [filas, columnas.join(","), porDia]); // eslint-disable-line react-hooks/exhaustive-deps
  const matriz = validas.map((p) => ({ p, celdas: columnas.map((c) => verdesDePregunta(p, grupos.get(c) || [], contar)) }))
    .filter((r) => r.celdas.some((c) => c.total > 0));
  if (!matriz.length || columnas.length < 2) return <div style={{ fontSize: 12.5, color: TXT.tenue }}>Hace falta un periodo de al menos 2 {porDia ? "días" : "semanas"} con respuestas.</div>;
  // rampa secuencial de verde (claro → oscuro) por % de respuestas en verde
  const color = (pct) => {
    if (pct == null) return "#f8fafc";
    const t = pct / 100, a = [240, 253, 244], b = [21, 128, 61];
    return `rgb(${a.map((ai, i) => Math.round(ai + (b[i] - ai) * t)).join(",")})`;
  };
  return (
    <div style={{ overflowX: "auto" }}>
      <table style={{ borderCollapse: "separate", borderSpacing: 3, minWidth: "100%" }}>
        <thead>
          <tr>
            <th style={{ textAlign: "left", fontSize: 11, color: TXT.tenue, fontWeight: 700, padding: "2px 6px" }}>Pregunta</th>
            {columnas.map((c) => (
              <th key={c} style={{ fontSize: 10.5, color: TXT.tenue, fontWeight: 700, padding: "2px 2px", whiteSpace: "nowrap" }}>
                {porDia ? `${c.slice(8, 10)}/${c.slice(5, 7)}` : `Sem ${c.slice(8, 10)}/${c.slice(5, 7)}`}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {matriz.map(({ p, celdas }) => (
            <tr key={p.id}>
              <td style={{ fontSize: 12, color: TXT.primario, fontWeight: 600, padding: "2px 6px", whiteSpace: "nowrap", maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis" }} title={p.subcategoria}>{p.emoji} {p.subcategoria}</td>
              {celdas.map((c, i) => (
                <td key={columnas[i]}
                  onMouseMove={(e) => setTip({ x: e.clientX, y: e.clientY, lines: [`${p.emoji || ""} ${p.subcategoria}`, porDia ? fmtDia(columnas[i]) : `Semana del ${fmtDia(columnas[i])}`, c.total ? `${c.verde} de ${c.total} en verde · ${c.pct}%` : "Sin respuestas"] })}
                  onMouseLeave={() => setTip(null)}
                  style={{ background: color(c.pct), borderRadius: 5, height: 26, minWidth: 40, textAlign: "center", fontSize: 11, fontWeight: 800, color: c.pct != null && c.pct >= 55 ? "#fff" : TXT.secundario }}>
                  {c.pct != null ? `${c.pct}%` : "—"}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 8, fontSize: 11, color: TXT.tenue }}>
        % de respuestas en verde · 0%
        {[10, 30, 50, 70, 90].map((v) => <span key={v} style={{ width: 18, height: 10, borderRadius: 3, background: color(v) }} />)}
        100%
      </div>
      <Tip tip={tip} />
    </div>
  );
}

// ── Atención: pisos con 0 reportes en el periodo ──────────────────────────
// filasSemana: filas del periodo seleccionado en el reporte. Un piso sale si tiene
// visitas obligatorias y ninguna enviada; si solo tiene reportes extra se
// muestra aparte (sí ha tenido visita, pero no en sus días de ruta).
async function copiarTexto(texto) {
  try { if (navigator.clipboard && window.isSecureContext) { await navigator.clipboard.writeText(texto); return true; } } catch (_) {}
  try {
    const ta = document.createElement("textarea");
    ta.value = texto; ta.style.position = "fixed"; ta.style.opacity = "0";
    document.body.appendChild(ta); ta.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return ok;
  } catch (_) { return false; }
}
const Chip = ({ p, mostrarAsistenta, extra }) => (
  <span title={extra ? `Sin reporte en sus ${p.total} visita(s) obligatoria(s), pero con ${p.extra} reporte(s) extra en el periodo` : `${p.total} visita${p.total !== 1 ? "s" : ""} obligatoria${p.total !== 1 ? "s" : ""} en el periodo, ninguna con reporte`} style={{
    background: "#fff", border: `1px solid ${extra ? "#ddd6fe" : "#fed7aa"}`, borderRadius: 10, padding: "5px 10px", fontSize: 12, color: extra ? "#4c1d95" : "#7c2d12",
  }}>
    <strong>{p.abreviatura}</strong>
    <span style={{ color: extra ? "#6d28d9" : "#c2410c" }}> · {p.supervisora}</span>
    {mostrarAsistenta && p.asistenta && <span style={{ color: extra ? "#5b21b6" : "#9a3412" }}> · {p.asistenta}</span>}
    <span style={{ color: extra ? COLOR_EXTRA : "#ea580c", fontWeight: 800 }}> · 0/{p.total}{extra ? ` · ✨${p.extra}` : ""}</span>
  </span>
);
// vistaAsistentas: en el reporte de asistentas añade la pestaña "Asistentas sin
// ningún reporte" (todas sus visitas obligatorias del periodo sin reporte).
export function AtencionSinReportes({ filasSemana, loading, error, mostrarAsistenta = false, vistaAsistentas = false }) {
  const [sup, setSup] = useState("todas");
  const [vista, setVista] = useState("pisos"); // "pisos" | "asistentas"
  const [copiado, setCopiado] = useState(null); // null | "ok" | "error"
  const { pisos, conExtra, asistentas } = useMemo(() => {
    const m = new Map();
    const ma = new Map();
    (filasSemana || []).forEach((f) => {
      const k = f.id_piso;
      if (!m.has(k)) m.set(k, { id_piso: k, abreviatura: f.abreviatura, supervisora: f.user_supervisora || "—", asistenta: null, total: 0, enviados: 0, extra: 0 });
      const e = m.get(k);
      // quien TENÍA que ir ese día (la sustituta si hay sustitución aplicada)
      const nombre = f.nombre_responsable || f.nombre_asistenta || f.asistenta;
      if (nombre && (!e.fechaAsistenta || f.fecha >= e.fechaAsistenta)) { e.asistenta = nombre; e.fechaAsistenta = f.fecha; } // la del día más reciente
      if (f.es_extra) { e.extra++; return; }
      e.total++; if (f.enviado) e.enviados++;
      if (vistaAsistentas && nombre) {
        const idA = f.id_responsable != null ? f.id_responsable : f.id_asistenta;
        const ka = idA != null ? `id:${idA}` : `n:${nombre}`;
        if (!ma.has(ka)) ma.set(ka, { clave: ka, nombre, supervisoras: new Set(), pisos: new Set(), total: 0, enviados: 0 });
        const ea = ma.get(ka);
        ea.supervisoras.add(f.user_supervisora || "—"); ea.pisos.add(f.abreviatura);
        ea.total++; if (f.enviado) ea.enviados++;
      }
    });
    const orden = (x, y) => y.total - x.total || String(x.abreviatura).localeCompare(String(y.abreviatura));
    const sinEnviar = Array.from(m.values()).filter((p) => p.total > 0 && p.enviados === 0);
    const asis = Array.from(ma.values()).filter((x) => x.total > 0 && x.enviados === 0)
      .map((x) => ({ ...x, supervisoras: [...x.supervisoras].sort(), pisos: [...x.pisos].sort() }))
      .sort((x, y) => y.total - x.total || x.nombre.localeCompare(y.nombre));
    return { pisos: sinEnviar.filter((p) => !p.extra).sort(orden), conExtra: sinEnviar.filter((p) => p.extra).sort(orden), asistentas: asis };
  }, [filasSemana, vistaAsistentas]);
  if (loading) return null;
  if (error) return null;
  const enAsistentas = vistaAsistentas && vista === "asistentas";
  const supDe = (x) => (enAsistentas ? x.supervisoras : [x.supervisora]);
  const base = enAsistentas ? asistentas : pisos;
  const supervisoras = [...new Set([...(enAsistentas ? asistentas : [...pisos, ...conExtra])].flatMap(supDe))].sort();
  const filtrar = (arr) => (sup === "todas" ? arr : arr.filter((x) => supDe(x).includes(sup)));
  const lista = filtrar(base), listaExtra = enAsistentas ? [] : filtrar(conExtra);
  const ok = base.length === 0;
  const limpiar = (v) => String(v ?? "").replace(/[\t\r\n]+/g, " ").trim();
  const copiar = async () => {
    const texto = enAsistentas
      ? ["Asistenta\tSupervisora", ...lista.map((x) => [x.nombre, x.supervisoras.join(", ")].map(limpiar).join("\t"))].join("\n")
      : ["Piso\tAsistenta\tSupervisora", ...lista.map((p) => [p.abreviatura, p.asistenta, p.supervisora].map(limpiar).join("\t"))].join("\n");
    const bien = await copiarTexto(texto);
    setCopiado(bien ? "ok" : "error");
    setTimeout(() => setCopiado(null), 2200);
  };
  const titulo = enAsistentas
    ? (ok ? "✅ En el periodo seleccionado todas las asistentas tienen al menos un reporte" : `⚡ Atención: ${base.length} asistenta${base.length !== 1 ? "s" : ""} sin ningún reporte en el periodo seleccionado`)
    : (ok ? "✅ En el periodo seleccionado todos los pisos tienen al menos un reporte" : `⚡ Atención: ${base.length} piso${base.length !== 1 ? "s" : ""} sin ningún reporte en el periodo seleccionado`);
  const pestaña = (k, texto, n) => (
    <button key={k} onClick={() => { setVista(k); setCopiado(null); }} style={{
      border: "none", borderRadius: 999, padding: "5px 12px", fontSize: 12.5, fontWeight: 800, cursor: "pointer",
      background: vista === k ? "#9a3412" : "transparent", color: vista === k ? "#fff" : "#9a3412",
    }}>{texto} <span style={{ opacity: 0.85 }}>({n})</span></button>
  );
  return (
    <div style={{ background: ok ? "#f0fdf4" : "#fff7ed", border: `1.5px solid ${ok ? "#86efac" : "#fdba74"}`, borderRadius: 14, padding: "12px 16px" }}>
      {vistaAsistentas && (
        <div style={{ display: "inline-flex", background: "#ffedd5", borderRadius: 999, padding: 3, border: "1.5px solid #fdba74", marginBottom: 10, flexWrap: "wrap" }}>
          {pestaña("pisos", "🏠 Pisos sin reporte", pisos.length)}
          {pestaña("asistentas", "👤 Asistentas sin ningún reporte", asistentas.length)}
        </div>
      )}
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <div style={{ flex: 1, minWidth: 200, fontWeight: 800, fontSize: 14.5, color: ok ? "#166534" : "#9a3412" }}>{titulo}</div>
        {supervisoras.length > 1 && (!ok || listaExtra.length > 0 || conExtra.length > 0) && (
          <select value={sup} onChange={(e) => setSup(e.target.value)} style={{
            padding: "7px 12px", borderRadius: 12, border: "2px solid #fdba74", fontSize: 12.5, background: "#fff",
            fontWeight: 800, color: "#9a3412", cursor: "pointer", outline: "none",
          }}>
            <option value="todas">Todas las supervisoras ({base.length})</option>
            {supervisoras.map((s) => <option key={s} value={s}>{s} ({base.filter((x) => supDe(x).includes(s)).length})</option>)}
          </select>
        )}
        {!ok && (
          <button onClick={copiar} disabled={!lista.length} title={enAsistentas ? "Copia la lista (Asistenta · Supervisora) para pegarla en Excel" : "Copia la lista (Piso · Asistenta · Supervisora) para pegarla en Excel"} style={{
            padding: "7px 12px", borderRadius: 12, border: "2px solid #fdba74", fontSize: 12.5, fontWeight: 800, cursor: lista.length ? "pointer" : "default",
            background: copiado === "ok" ? "#dcfce7" : copiado === "error" ? "#fee2e2" : "#fff",
            color: copiado === "ok" ? "#166534" : copiado === "error" ? "#b91c1c" : "#9a3412",
          }}>{copiado === "ok" ? `✅ Copiados ${lista.length}` : copiado === "error" ? "❌ No se pudo copiar" : "📋 Copiar para Excel"}</button>
        )}
      </div>
      {!ok && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 10, maxHeight: 180, overflowY: "auto" }}>
          {!lista.length ? <span style={{ fontSize: 12.5, color: "#9a3412" }}>Ninguno de esta supervisora.</span>
            : enAsistentas ? lista.map((x) => (
              <span key={x.clave} title={`Pisos: ${x.pisos.join(", ")} · ${x.total} visita${x.total !== 1 ? "s" : ""} obligatoria${x.total !== 1 ? "s" : ""} en el periodo, ninguna con reporte`} style={{
                background: "#fff", border: "1px solid #fed7aa", borderRadius: 10, padding: "5px 10px", fontSize: 12, color: "#7c2d12",
              }}>
                <strong>👤 {x.nombre}</strong>
                <span style={{ color: "#c2410c" }}> · {x.supervisoras.join(", ")}</span>
                <span style={{ color: "#ea580c", fontWeight: 800 }}> · {x.pisos.length} piso{x.pisos.length !== 1 ? "s" : ""} · 0/{x.total}</span>
              </span>
            ))
            : lista.map((p) => <Chip key={p.id_piso} p={p} mostrarAsistenta={mostrarAsistenta} />)}
        </div>
      )}
      {listaExtra.length > 0 && (
        <div style={{ marginTop: 12 }}>
          <div style={{ fontSize: 12.5, fontWeight: 800, color: "#6d28d9", marginBottom: 6 }}>
            ✨ Aparte: {listaExtra.length} piso{listaExtra.length !== 1 ? "s" : ""} sin reporte en sus días de ruta, pero con algún reporte extra en el periodo
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, maxHeight: 140, overflowY: "auto" }}>
            {listaExtra.map((p) => <Chip key={p.id_piso} p={p} mostrarAsistenta={mostrarAsistenta} extra />)}
          </div>
        </div>
      )}
    </div>
  );
}

// Estadística diaria {fecha, enviados, total, extra, pct} a partir de filas
// obligatorias (+ extras opcionales, que solo se cuentan aparte)
export function statsDias(filasMand, filasExtra = []) {
  const m = new Map();
  const nuevo = (fecha) => ({ fecha, enviados: 0, total: 0, extra: 0 });
  filasMand.forEach((f) => { if (!m.has(f.fecha)) m.set(f.fecha, nuevo(f.fecha)); const e = m.get(f.fecha); e.total++; if (f.enviado) e.enviados++; });
  filasExtra.forEach((f) => { if (!m.has(f.fecha)) m.set(f.fecha, nuevo(f.fecha)); m.get(f.fecha).extra++; });
  return Array.from(m.values()).map((e) => ({ ...e, pct: e.total ? Math.round((e.enviados / e.total) * 100) : null }))
    .sort((a, b) => a.fecha.localeCompare(b.fecha));
}

// Panel que se abre al pulsar una supervisora en "Cumplimiento por supervisora":
// su evolución en línea y, si se pasa subValor (reporte de asistentas), el
// cumplimiento de cada una de sus asistentas con su mini-tendencia.
export function DetalleSupervisora({ dias, media, filasSup = [], subValor, Barra }) {
  const subs = useMemo(() => {
    if (!subValor) return [];
    const m = new Map();
    filasSup.forEach((f) => { const k = subValor(f) || "—"; if (!m.has(k)) m.set(k, []); m.get(k).push(f); });
    return Array.from(m.entries()).map(([nombre, fs]) => {
      const serie = statsDias(fs);
      const enviados = fs.filter((f) => f.enviado).length;
      return { nombre, serie, enviados, total: fs.length, pct: fs.length ? Math.round((enviados / fs.length) * 100) : null };
    }).sort((a, b) => (a.pct ?? 101) - (b.pct ?? 101) || a.nombre.localeCompare(b.nombre));
  }, [filasSup, subValor]);
  return (
    <div style={{ margin: "2px 0 12px", padding: "12px 14px", background: "#f8fafc", border: "1px solid #e0e7ff", borderRadius: 12 }}>
      {dias.length > 1 ? <EvolucionCumplimiento dias={dias} media={media} /> : <div style={{ fontSize: 12.5, color: TXT.tenue }}>Solo hay un día con visitas en el periodo.</div>}
      {subs.length > 0 && (
        <div style={{ marginTop: 12 }}>
          <div style={{ fontSize: 12.5, fontWeight: 800, color: TXT.secundario, marginBottom: 6 }}>👤 Sus asistentas <span style={{ fontWeight: 500, color: TXT.tenue }}>(de menos a más cumplimiento)</span></div>
          {subs.map((s) => (
            <div key={s.nombre} style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <div style={{ flex: 1, minWidth: 0 }}><Barra label={s.nombre} enviados={s.enviados} total={s.total} pct={s.pct} /></div>
              <MiniTendencia serie={s.serie} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Evolución de las franjas de "Respeto de horarios": una columna 100 % apilada
// por día (o por semana si el periodo pasa de 45 días).
export function EvolucionFranjas({ filas, franja, franjas, alto = 90 }) {
  const [tip, setTip] = useState(null);
  const puntos = useMemo(() => {
    const fechas = [...new Set(filas.map((f) => f.fecha))].sort();
    const porSemana = fechas.length > 45;
    const m = new Map();
    filas.forEach((f) => {
      const fr = franja(f); if (!fr) return;
      const k = porSemana ? lunesDe(f.fecha) : f.fecha;
      if (!m.has(k)) m.set(k, { clave: k, porSemana, total: 0, ...Object.fromEntries(franjas.map((x) => [x.k, 0])) });
      const e = m.get(k); e[fr]++; e.total++;
    });
    return Array.from(m.values()).sort((a, b) => a.clave.localeCompare(b.clave));
  }, [filas, franja, franjas]);
  if (puntos.length < 2) return <div style={{ fontSize: 12, color: TXT.tenue }}>Hace falta más de un día con reportes para ver la evolución.</div>;
  const H = alto;
  const anchoMax = alto >= 150 ? 48 : 28;
  const cada = Math.max(1, Math.ceil(puntos.length / (alto >= 150 ? 14 : 10)));
  const conEtiquetas = alto >= 150 && puntos.length <= 20; // % dentro de cada trozo si hay sitio
  return (
    <div>
      <div style={{ display: "flex", alignItems: "flex-end", gap: 2, height: H, borderBottom: `1px solid ${TXT.rejilla}` }}>
        {puntos.map((p) => (
          <div key={p.clave}
            onMouseMove={(e) => setTip({ x: e.clientX, y: e.clientY, lines: [p.porSemana ? `Semana del ${fmtDia(p.clave)}` : fmtDia(p.clave), ...franjas.map((x) => `${x.icono} ${x.label}: ${p[x.k]} (${Math.round((p[x.k] / p.total) * 100)}%)`)] })}
            onMouseLeave={() => setTip(null)}
            style={{ flex: 1, minWidth: 3, maxWidth: anchoMax, height: "100%", display: "flex", flexDirection: "column-reverse", gap: 1, cursor: "default" }}>
            {franjas.map((x) => p[x.k] > 0 && (
              <div key={x.k} style={{ height: `${(p[x.k] / p.total) * 100}%`, background: x.color, borderRadius: 2, display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden" }}>
                {conEtiquetas && (p[x.k] / p.total) >= 0.12 && <span style={{ color: "#fff", fontSize: 10.5, fontWeight: 800 }}>{Math.round((p[x.k] / p.total) * 100)}%</span>}
              </div>
            ))}
          </div>
        ))}
      </div>
      <div style={{ display: "flex", gap: 2, marginTop: 3 }}>
        {puntos.map((p, i) => (
          <div key={p.clave} style={{ flex: 1, minWidth: 3, maxWidth: anchoMax, fontSize: alto >= 150 ? 11 : 9.5, color: TXT.tenue, textAlign: "center", whiteSpace: "nowrap", overflow: "visible" }}>
            {(i % cada === 0 || i === puntos.length - 1) ? `${p.clave.slice(8, 10)}/${p.clave.slice(5, 7)}` : ""}
          </div>
        ))}
      </div>
      <Tip tip={tip} />
    </div>
  );
}

// ── Filtro de empresa (CH / CH-SS / CM) para tabla y resumen ──────────────
export const EMPRESAS_REPORTE = ["CH", "CH-SS", "CM"];
export const EMPRESAS_POR_DEFECTO = ["CH"];
// conteo: {empresa: nº de pisos} del periodo, para mostrar al lado de cada opción
export function SelectorEmpresas({ seleccion, onChange, conteo = {} }) {
  const [abierto, setAbierto] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!abierto) return;
    const fuera = (e) => { if (ref.current && !ref.current.contains(e.target)) setAbierto(false); };
    document.addEventListener("mousedown", fuera);
    document.addEventListener("touchstart", fuera);
    return () => { document.removeEventListener("mousedown", fuera); document.removeEventListener("touchstart", fuera); };
  }, [abierto]);
  const todas = EMPRESAS_REPORTE.every((e) => seleccion.includes(e));
  const alternar = (emp) => {
    const nuevo = seleccion.includes(emp) ? seleccion.filter((x) => x !== emp) : [...seleccion, emp];
    if (nuevo.length) onChange(EMPRESAS_REPORTE.filter((e) => nuevo.includes(e))); // siempre al menos una
  };
  const texto = todas ? "Todas" : seleccion.join(" + ");
  const fila = (activo, etiqueta, n, onClick, key) => (
    <button key={key} type="button" onClick={onClick} style={{
      display: "flex", alignItems: "center", gap: 10, width: "100%", padding: "9px 10px", borderRadius: 10, border: "none",
      background: activo ? "#eef2ff" : "transparent", cursor: "pointer", fontSize: 14, fontWeight: 700, color: "#1e293b", textAlign: "left",
    }}>
      <span style={{
        width: 20, height: 20, borderRadius: 6, flexShrink: 0, display: "inline-flex", alignItems: "center", justifyContent: "center",
        border: `2px solid ${activo ? "#4f46e5" : "#94a3b8"}`, background: activo ? "#4f46e5" : "#fff", color: "#fff", fontSize: 13,
      }}>{activo ? "✓" : ""}</span>
      <span style={{ flex: 1 }}>{etiqueta}</span>
      {n != null && <span style={{ fontSize: 12, color: "#64748b", fontWeight: 600 }}>{n} piso{n !== 1 ? "s" : ""}</span>}
    </button>
  );
  return (
    <div ref={ref} style={{ position: "relative" }}>
      <button type="button" onClick={() => setAbierto((a) => !a)} title="Empresas que se incluyen en la tabla y en el resumen" style={{
        padding: "12px 16px", borderRadius: 14, border: `2px solid ${abierto ? "#6366f1" : "#c7d2fe"}`, fontSize: 15,
        background: "linear-gradient(135deg,#fff,#eef2ff)", fontWeight: 800, color: "#3730a3", cursor: "pointer",
        boxShadow: "0 3px 10px rgba(79,70,229,0.15)", display: "inline-flex", alignItems: "center", gap: 8, whiteSpace: "nowrap",
      }}>
        🏢 Empresa: <span style={{ background: "#4338ca", color: "#fff", borderRadius: 999, padding: "1px 10px", fontSize: 13 }}>{texto}</span>
        <span style={{ fontSize: 12, transition: "transform .2s", transform: abierto ? "rotate(180deg)" : "none" }}>▾</span>
      </button>
      {abierto && (
        <div style={{
          position: "absolute", top: "calc(100% + 6px)", left: 0, zIndex: 60, minWidth: 230, background: "#fff", borderRadius: 14,
          border: "1.5px solid #e0e7ff", boxShadow: "0 12px 32px rgba(15,23,42,0.18)", padding: 6,
        }}>
          {fila(todas, "Todas", null, () => onChange(todas ? EMPRESAS_POR_DEFECTO : [...EMPRESAS_REPORTE]), "todas")}
          <div style={{ height: 1, background: "#e2e8f0", margin: "4px 6px" }} />
          {EMPRESAS_REPORTE.map((emp) => fila(seleccion.includes(emp), emp, conteo[emp] ?? 0, () => alternar(emp), emp))}
        </div>
      )}
    </div>
  );
}
